import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Stethoscope, Mail, Phone, MessageSquare, ChevronDown, ChevronUp, ArrowLeft } from 'lucide-react';

const faqs = [
  {
    q: 'How do I add a doctor to my care team?',
    a: 'After creating an account, go to your Care Team page. You can search for a doctor by name or email and send them a care team invitation. Your doctor will receive a notification and can accept or decline.',
  },
  {
    q: 'Is my health data secure and private?',
    a: 'Yes. SymptoConnect is HIPAA-compliant and uses end-to-end encryption for all health data. You control exactly which providers can access which parts of your record through granular consent settings.',
  },
  {
    q: 'Can I revoke a doctor\'s access to my data?',
    a: 'Absolutely. Go to My Data & Privacy → Doctor Data Access and revoke or suspend consent at any time. This takes effect immediately.',
  },
  {
    q: 'What happens if I log a symptom marked as urgent?',
    a: 'Urgent symptoms immediately trigger a risk assessment and notify your care team. Clinicians on your team receive a priority alert and can respond directly within the platform.',
  },
  {
    q: 'Is SymptoConnect a replacement for emergency services?',
    a: 'No. SymptoConnect is a care coordination and monitoring platform — not a replacement for emergency services. If you are experiencing a medical emergency, call 911 immediately.',
  },
  {
    q: 'How do I cancel or delete my account?',
    a: 'Go to Settings → Account. You can deactivate your account or submit a data deletion request. We process deletion requests within 30 days as required by applicable law.',
  },
];

const ContactPage = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [sent, setSent] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    // In production this would POST to a backend or email service
    setSent(true);
  };

  return (
    <div className="min-h-screen font-sans" style={{ background: '#f9f9fd', color: '#191c1e' }}>
      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-black/5">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg,#00478d,#005eb8)' }}>
              <Stethoscope className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="font-bold text-base tracking-tight" style={{ color: '#191c1e' }}>
              Sympto<span style={{ color: '#00478d' }}>Connect</span>
            </span>
          </Link>
          <Link to="/" className="flex items-center gap-1.5 text-sm font-semibold hover:text-[#00478d] transition-colors" style={{ color: '#424752' }}>
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-14 pb-6 text-center">
        <h1 className="font-extrabold mb-3" style={{ fontSize: 'clamp(1.75rem,4vw,2.75rem)', letterSpacing: '-0.02em', color: '#191c1e' }}>
          Help Center & Contact
        </h1>
        <p className="text-base sm:text-lg max-w-xl mx-auto" style={{ color: '#424752' }}>
          Questions, feedback, or urgent concerns — we're here for you.
        </p>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-20 space-y-16">

        {/* Contact methods */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            {
              icon: Mail,
              title: 'Email Support',
              desc: 'For non-urgent questions and account help.',
              link: 'mailto:support@symptoconnect.com',
              label: 'support@symptoconnect.com',
              avail: 'Response within 24 hours',
              color: '#d6e3ff',
              iconColor: '#00478d',
            },
            {
              icon: Phone,
              title: 'Clinical Urgent Line',
              desc: 'For urgent clinical concerns from providers.',
              link: 'tel:+18002787866',
              label: '1-800-CARE-NOW',
              avail: 'Available 24/7',
              color: '#97f2f3',
              iconColor: '#007072',
            },
            {
              icon: MessageSquare,
              title: 'In-App Messaging',
              desc: 'Message your care team directly in the platform.',
              link: '/login',
              label: 'Open the app',
              avail: 'Real-time response',
              color: '#ffdea8',
              iconColor: '#5f4300',
            },
          ].map((c) => {
            const Icon = c.icon;
            return (
              <div key={c.title} className="rounded-2xl p-6" style={{ background: '#ffffff', boxShadow: '0px 4px 16px rgba(25,28,30,0.06)' }}>
                <div className="w-11 h-11 rounded-2xl flex items-center justify-center mb-4" style={{ background: c.color }}>
                  <Icon className="w-5 h-5" style={{ color: c.iconColor }} />
                </div>
                <h3 className="font-bold text-sm mb-1" style={{ color: '#191c1e' }}>{c.title}</h3>
                <p className="text-xs mb-3 leading-relaxed" style={{ color: '#727783' }}>{c.desc}</p>
                <a href={c.link} className="text-xs font-bold block mb-1 hover:underline" style={{ color: c.iconColor }}>{c.label}</a>
                <p className="text-xs" style={{ color: '#727783' }}>{c.avail}</p>
              </div>
            );
          })}
        </div>

        {/* Contact form + FAQ side-by-side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">

          {/* Contact form */}
          <div>
            <h2 className="font-bold text-xl mb-6" style={{ color: '#191c1e' }}>Send us a message</h2>
            {sent ? (
              <div className="rounded-2xl p-8 text-center" style={{ background: '#97f2f3' }}>
                <p className="font-bold text-lg mb-2" style={{ color: '#007072' }}>Message received!</p>
                <p className="text-sm" style={{ color: '#007072' }}>We'll get back to you within 24 hours at the email you provided.</p>
                <button onClick={() => { setSent(false); setForm({ name: '', email: '', subject: '', message: '' }); }} className="mt-4 text-xs font-bold underline" style={{ color: '#007072' }}>Send another</button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-bold mb-1" style={{ color: '#424752' }}>Name</label>
                    <input
                      required
                      value={form.name}
                      onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                      placeholder="Your name"
                      className="w-full px-3 py-2.5 text-sm rounded-xl border focus:outline-none focus:ring-2"
                      style={{ borderColor: '#e1e2e6', background: '#fff', focusRingColor: '#00478d' }}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold mb-1" style={{ color: '#424752' }}>Email</label>
                    <input
                      required
                      type="email"
                      value={form.email}
                      onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                      placeholder="you@example.com"
                      className="w-full px-3 py-2.5 text-sm rounded-xl border focus:outline-none focus:ring-2"
                      style={{ borderColor: '#e1e2e6', background: '#fff' }}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold mb-1" style={{ color: '#424752' }}>Subject</label>
                  <select
                    value={form.subject}
                    onChange={e => setForm(p => ({ ...p, subject: e.target.value }))}
                    className="w-full px-3 py-2.5 text-sm rounded-xl border focus:outline-none focus:ring-2"
                    style={{ borderColor: '#e1e2e6', background: '#fff' }}
                  >
                    <option value="">Select a topic...</option>
                    <option>Account help</option>
                    <option>Technical issue</option>
                    <option>Data & privacy</option>
                    <option>Billing</option>
                    <option>Clinical partnership</option>
                    <option>Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold mb-1" style={{ color: '#424752' }}>Message</label>
                  <textarea
                    required
                    value={form.message}
                    onChange={e => setForm(p => ({ ...p, message: e.target.value }))}
                    placeholder="Describe your question or issue..."
                    rows={5}
                    className="w-full px-3 py-2.5 text-sm rounded-xl border focus:outline-none focus:ring-2 resize-none"
                    style={{ borderColor: '#e1e2e6', background: '#fff' }}
                  />
                </div>
                <button
                  type="submit"
                  className="w-full py-3 rounded-xl font-bold text-sm text-white transition-all hover:shadow-lg"
                  style={{ background: 'linear-gradient(135deg,#00478d,#005eb8)' }}
                >
                  Send Message
                </button>
                <p className="text-xs text-center" style={{ color: '#727783' }}>
                  We do not share your information with third parties. See our{' '}
                  <Link to="/privacy" className="underline" style={{ color: '#00478d' }}>Privacy Policy</Link>.
                </p>
              </form>
            )}
          </div>

          {/* FAQ */}
          <div>
            <h2 className="font-bold text-xl mb-6" style={{ color: '#191c1e' }}>Frequently asked questions</h2>
            <div className="space-y-2">
              {faqs.map((faq, i) => (
                <div key={i} className="rounded-xl overflow-hidden" style={{ background: '#ffffff', boxShadow: '0px 2px 8px rgba(25,28,30,0.04)' }}>
                  <button
                    onClick={() => setOpenFaq(o => o === i ? null : i)}
                    className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-slate-50 transition-colors gap-3"
                  >
                    <span className="text-sm font-semibold" style={{ color: '#191c1e' }}>{faq.q}</span>
                    {openFaq === i
                      ? <ChevronUp className="w-4 h-4 flex-shrink-0" style={{ color: '#727783' }} />
                      : <ChevronDown className="w-4 h-4 flex-shrink-0" style={{ color: '#727783' }} />
                    }
                  </button>
                  {openFaq === i && (
                    <div className="px-5 pb-4">
                      <p className="text-sm leading-relaxed" style={{ color: '#424752' }}>{faq.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-8" style={{ background: '#191c1e', color: 'rgba(255,255,255,0.5)' }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
          <p>© 2026 SymptoConnect. All rights reserved.</p>
          <div className="flex gap-5">
            <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
            <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ContactPage;
